import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState<{
    type: "idle" | "success" | "error";
    message: string;
  }>({ type: "idle", message: "" });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token");
    setToken(t);
    if (!t) {
      setStatus({
        type: "error",
        message: "This reset link is invalid. Please request a new one.",
      });
    }
  }, [location.search]);

  const validatePassword = (value: string) => {
    if (value.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token) {
      setStatus({
        type: "error",
        message: "This reset link is invalid. Please request a new one.",
      });
      return;
    }

    const pwdError = validatePassword(newPassword);
    if (pwdError) {
      setStatus({ type: "error", message: pwdError });
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus({
        type: "error",
        message: "Passwords do not match.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "idle", message: "" });

    try {
      const response = await fetch(buildApiUrl("/api/auth/reset-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        setStatus({
          type: "success",
          message: "Password reset successful. You can now log in.",
        });
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1500);
      } else if (response.status >= 400 && response.status < 500) {
        // For invalid/expired/already-used token we show a generic message
        setStatus({
          type: "error",
          message:
            "This reset link is invalid or has expired. Please request a new one.",
        });
      } else {
        setStatus({
          type: "error",
          message:
            data?.message ||
            "We couldn’t reset your password right now. Please try again.",
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordHint = "Use at least 8 characters.";

  const isFormDisabled = !token || isSubmitting;

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 16px 40px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background:
            "linear-gradient(145deg, #4E1C81 0%, #AB68C5 100%)",
          borderRadius: 24,
          padding: 32,
          boxShadow: "0 30px 80px rgba(15,23,42,0.8)",
          border: "1px solid rgba(171,104,197,0.7)",
          color: "#e5e7eb",
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{
            border: "none",
            background: "rgba(15,23,42,0.5)",
            color: "rgba(226,232,240,0.9)",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 20,
            cursor: "pointer",
            padding: "6px 10px",
            borderRadius: 999,
          }}
        >
          <span style={{ fontSize: 16 }}>←</span> Back to login
        </button>

        <h1
          style={{
            fontSize: 28,
            fontWeight: 800,
            marginBottom: 8,
            color: "#eef2ff",
          }}
        >
          Set a new password
        </h1>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.7,
            color: "rgba(199,210,254,0.95)",
            marginBottom: 20,
          }}
        >
          Choose a strong password to secure your EventNova account.
        </p>

        {status.type === "success" && (
          <div
            style={{
              background: "rgba(22,163,74,0.18)",
              color: "#bbf7d0",
              borderRadius: 12,
              padding: "10px 14px",
              border: "1px solid rgba(34,197,94,0.55)",
              fontSize: 13,
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            {status.message}
          </div>
        )}

        {status.type === "error" && (
          <div
            style={{
              background: "rgba(248,113,113,0.16)",
              color: "#fecaca",
              borderRadius: 12,
              padding: "10px 14px",
              border: "1px solid rgba(248,113,113,0.7)",
              fontSize: 13,
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            {status.message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color: "#c7d2fe",
              }}
            >
              New password
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(15,23,42,0.9)",
                borderRadius: 10,
                border: "1px solid rgba(129,140,248,0.9)",
                paddingRight: 8,
              }}
            >
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter a strong password"
                disabled={isFormDisabled}
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: "transparent",
                  color: "#e5e7eb",
                  fontSize: 14,
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                disabled={isFormDisabled}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#c7d2fe",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  cursor: isFormDisabled ? "not-allowed" : "pointer",
                  padding: "0 4px",
                }}
              >
                {showNewPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color: "#c7d2fe",
              }}
            >
              Confirm password
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(15,23,42,0.9)",
                borderRadius: 10,
                border: "1px solid rgba(129,140,248,0.9)",
                paddingRight: 8,
              }}
            >
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                disabled={isFormDisabled}
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: "transparent",
                  color: "#e5e7eb",
                  fontSize: 14,
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                disabled={isFormDisabled}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#c7d2fe",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  cursor: isFormDisabled ? "not-allowed" : "pointer",
                  padding: "0 4px",
                }}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <p
            style={{
              fontSize: 12,
              color: "rgba(191,219,254,0.9)",
              marginBottom: 16,
              lineHeight: 1.6,
            }}
          >
            {passwordHint}
          </p>

          <button
            type="submit"
            disabled={
              isFormDisabled ||
              !newPassword.trim() ||
              !confirmPassword.trim()
            }
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: 12,
              border: "none",
              fontSize: 15,
              fontWeight: 700,
              color: "#f9fafb",
              background:
                isFormDisabled ||
                !newPassword.trim() ||
                !confirmPassword.trim()
                  ? "rgba(171,104,197,0.5)"
                  : "linear-gradient(135deg, #4E1C81 0%, #AB68C5 100%)",
              cursor:
                isFormDisabled ||
                !newPassword.trim() ||
                !confirmPassword.trim()
                  ? "not-allowed"
                  : "pointer",
              boxShadow:
                isFormDisabled ||
                !newPassword.trim() ||
                !confirmPassword.trim()
                  ? "none"
                  : "0 18px 40px rgba(15,23,42,0.9)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            {isSubmitting ? "Updating password..." : "Reset password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;


